import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';
import { SoundPreferences } from '@/utils/SoundManager';

export interface EmailNotificationPreferences {
  profilePurchase?: boolean;
  profileSale?: boolean;
  newMessage?: boolean;
  newComment?: boolean;
  newLike?: boolean;
  newFollow?: boolean;
  mentionInPost?: boolean;
  /** #297: emails when a creator you subscribe to publishes a new
   *  paid or PPV post. Defaults to true server-side. */
  newPostFromSubscription?: boolean;
}

interface UserPreferences {
  theme?: 'light' | 'dark';
  notifications?: {
    email?: boolean;
    push?: boolean;
  };
  soundPreferences?: SoundPreferences;
  emailNotifications?: EmailNotificationPreferences;
  distanceUnit?: 'miles' | 'km';
  homeId?: string;
  touringId?: string;
}

// Update user theme preference
export const updateThemePreference = createAsyncThunk(
  'user/updateTheme',
  async (theme: 'light' | 'dark', { rejectWithValue }) => {
    try {
      const response = await api.patch('/user/preferences', { theme });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update theme';
      return rejectWithValue(message);
    }
  }
);

// Fetch user preferences
export const fetchUserPreferences = createAsyncThunk(
  'user/fetchPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<{ preferences: UserPreferences }>('/user/preferences');
      return response.data.preferences;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch preferences';
      return rejectWithValue(message);
    }
  }
);

// Update multiple preferences at once
export const updateUserPreferences = createAsyncThunk(
  'user/updatePreferences',
  async (preferences: Partial<UserPreferences>, { rejectWithValue }) => {
    try {
      const response = await api.patch('/user/preferences', preferences);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update preferences';
      return rejectWithValue(message);
    }
  }
);

// Update sound preferences
export const updateSoundPreferences = createAsyncThunk(
  'user/updateSoundPreferences',
  async (soundPreferences: Partial<SoundPreferences>, { rejectWithValue }) => {
    try {
      const response = await api.patch('/user/preferences', { soundPreferences });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update sound preferences';
      return rejectWithValue(message);
    }
  }
);

// Fetch sound preferences
export const fetchSoundPreferences = createAsyncThunk(
  'user/fetchSoundPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<{ preferences: UserPreferences }>('/user/preferences');
      return response.data.preferences.soundPreferences;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch sound preferences';
      return rejectWithValue(message);
    }
  }
);

// Update email notification preferences
export const updateEmailNotificationPreferences = createAsyncThunk(
  'user/updateEmailNotificationPreferences',
  async (emailNotifications: Partial<EmailNotificationPreferences>, { rejectWithValue }) => {
    try {
      const response = await api.patch('/user/preferences', { emailNotifications });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update email notification preferences';
      return rejectWithValue(message);
    }
  }
);

// Fetch email notification preferences
export const fetchEmailNotificationPreferences = createAsyncThunk(
  'user/fetchEmailNotificationPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<{ preferences: UserPreferences }>('/user/preferences');
      return response.data.preferences.emailNotifications;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch email notification preferences';
      return rejectWithValue(message);
    }
  }
);
